"use client";

import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { ChevronDown } from "lucide-react";

type AccordionItem = {
  id: number;
  title: string;
  content: string;
};

const data: AccordionItem[] = [
  {
    id: 1,
    title: "What software and mobile app development services do you offer?",
    content: "We build robust, cloud agnostic applications focusing on cross-platform stacks without compromising native UX in the following categories: <ul class='accordian-list'><li>Web applications</li> <li>Android/iOS apps</li> <li>APIs and integrations</li> <li>Progressive Web Apps (PWAs)</li></ul> Our teams cover end-to-end delivery—UX research and UI design, development, testing, deployment, and post-launch support. We can modernize legacy apps or start net-new builds. Everything is instrumented for performance and observability from day one.</br></br>",
  },
  {
    id: 2,
    title: "Can you integrate AI into my existing product?",
    content: "Yes—safely and pragmatically. We add AI features such as recommendations, NLP, anomaly detection, or forecasting using provider models or open-source options. We set up data pipelines, guardrails, human-in-the-loop workflows, and monitoring to ensure the models improve over time.",
  },
  {
    id: 3,
    title: "What is your DevOps and Cloud practice like?",
    content: "We implement CI/CD, Infrastructure as Code (Terraform/Ansible), and container orchestration (Docker/Kubernetes). Environments are reproducible, secrets are managed, and releases use blue/green or canary. We are cloud-agnostic by design. We operate on AWS, Azure, and GCP with monitoring, backups, patches, and monthly cost/health reports. We'll help you choose the right services per workload and avoid lock-in where possible. Multi-cloud and cost optimization are part of the plan. We also set SLOs and autoscaling so performance stays predictable under load.",
  },
  {
    id: 4,
    title: "How do you ensure security and quality of the software you deliver?",
    content: "Security is baked in: code reviews, dependency scanning, secret management, role-based access, and environment isolation. Quality comes from automated tests (unit, API, UI), gated CI/CD pipelines, and performance checks. We align with OWASP and provide security test reports before go-live.",
  },
  {
    id: 5,
    title: "What's your approach to project management and communication?",
    content: "You get a dedicated PM/Scrum Lead, a sprint plan, and a clear roadmap. We work in two-week sprints with demos, burn-down charts, and stakeholder updates. Day-to-day we use Slack/Teams, Jira, and GitHub/GitLab; you have full visibility into tasks, code, and releases.",
  },
  {
    id: 6,
    title: "Do you assist with software integration and data migration?",
    content: "Absolutely. We design and build APIs, integrate third-party services (payments, identity, CRM, analytics), and migrate data with rollback plans and audit trails. For cloud moves, we handle lift-and-shift and re-platforming with minimal downtime and clear cutover steps. We recommend the approach after a quick discovery on your performance, budget, and roadmap needs.",
  },
  {
    id: 7,
    title: "How do you handle monitoring, incident response and post-launch maintenance?",
    content: "We deliver a unified dashboard for app + infra metrics (great for your central systems), centralized logging, alerting tuned to user impact, and synthetics/uptime checks. On-call runbooks and escalation paths are documented. Post-incident reviews lead to permanent fixes, not recurring alarms. We handle post-launch maintenance through support SLAs. We cover monitoring, bug fixes, security updates, and minor enhancements, with agreed response and resolution targets. You'll also get monthly reports on uptime, performance, and backlog health.",
  },
  {
    id: 8,
    title: "Can you work with my in-house team?",
    content: "Definitely. We can augment your team or take full ownership of a workstream. Shared rituals (stand-ups, design reviews, joint PRs) keep everyone aligned. We tailor ways of working to your processes and tools.",
  },
];

export default function Accordion() {
  const [openId, setOpenId] = useState<number | null>(null);

  const toggleAccordion = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="accordian-container">
      {data.map((item) => (
        <div key={item.id} className="accordian-item">
          <button
            onClick={() => toggleAccordion(item.id)}
            className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            suppressHydrationWarning
          >
            <div className="flex items-center gap-3">
              {/* <FontAwesomeIcon icon={faPaperPlane} className="text-gray-600" /> */}
              {item.id}
              <span className="font-medium text-gray-800">{item.title}</span>
            </div>
            <ChevronDown
              className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${
                openId === item.id ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Content with animation */}
          <div
            className={`px-12 overflow-hidden transition-all duration-500 ease-in-out ${
              openId === item.id ? " py-4" : "max-h-0"
            }`}
          >
            <div
              className="text-gray-600"
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
